import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  CreateManualOrderBody,
  ManualOrderResult,
  MerchantDrivenStatus,
  MerchantOrder,
  MerchantOrderPage,
  OrderSource,
} from './types';

import { apiRequest, type ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

/** Page size for the order list — matches the API's own default (DT-5 / P1-3). */
export const ORDERS_PAGE_SIZE = 50;

/** The real maximum `GET /merchant/orders` accepts (`order.dto.ts`'s `limit`), used by full-history fetches below to minimise page count. */
const ALL_ORDERS_FETCH_LIMIT = 200;
/**
 * A real ceiling, not a guess at "enough": up to 2,000 of the most
 * recent orders are scanned before a full-history read (Clients,
 * Paiements & COD) gives up paginating. A shop with more than 2,000
 * orders ever placed is far beyond anything this environment's real
 * data has ever produced — and reaching this ceiling only NARROWS the
 * scope of what was aggregated, it never fabricates a figure for the
 * orders left unscanned. `AllOrdersResult.truncated` says so honestly
 * when it happens.
 */
const ALL_ORDERS_MAX_PAGES = 10;

export interface AllOrdersResult {
  readonly orders: readonly MerchantOrder[];
  /** True once `ALL_ORDERS_MAX_PAGES` was reached without exhausting the tenant's real order history — the aggregate below is then a "most recent N orders" view, not "every order ever placed". */
  readonly truncated: boolean;
  readonly isPending: boolean;
  readonly isError: boolean;
}

/**
 * The tenant's full order history (as far as `ALL_ORDERS_MAX_PAGES`
 * allows), for screens that aggregate ACROSS orders rather than list a
 * page of them — Clients (group by phone) and Paiements & COD (group
 * by payment-relevant status). Real data only: paginates the same
 * `GET /merchant/orders` every other order screen already calls,
 * never a second, invented endpoint. One shared `queryKey` so both
 * screens reuse a single fetch when visited in the same session.
 */
export function useAllOrdersQuery(): AllOrdersResult {
  const { accessToken, activeShopId } = useSession();

  const query = useQuery<{ orders: readonly MerchantOrder[]; truncated: boolean }, ApiError>({
    queryKey: ['orders', activeShopId, 'all'],
    queryFn: async () => {
      const collected: MerchantOrder[] = [];
      let offset = 0;
      let truncated = false;
      for (let page = 0; page < ALL_ORDERS_MAX_PAGES; page += 1) {
        const result = await apiRequest<MerchantOrderPage>(
          `/merchant/orders?limit=${String(ALL_ORDERS_FETCH_LIMIT)}&offset=${String(offset)}`,
          { accessToken, shopId: activeShopId },
        );
        collected.push(...result.orders);
        const reachedEnd = offset + result.orders.length >= result.total;
        if (reachedEnd) break;
        if (page === ALL_ORDERS_MAX_PAGES - 1) {
          truncated = true;
          break;
        }
        offset += ALL_ORDERS_FETCH_LIMIT;
      }
      return { orders: collected, truncated };
    },
    enabled: accessToken !== null && activeShopId !== null,
  });

  return {
    orders: query.data?.orders ?? [],
    truncated: query.data?.truncated ?? false,
    isPending: query.isPending,
    isError: query.isError,
  };
}

/**
 * `GET /api/v1/merchant/orders?status=&limit=&offset=` — DEV §9.4,
 * `@RequirePermission('orders:write')`. Paginated since DT-5 / P1-3: the
 * response is a bounded page, not the tenant's whole order history.
 * `offset` is part of the query key so paging refetches;
 * `placeholderData` avoids a skeleton flash between pages — the same
 * pattern `useMerchantsQuery` (admin-dashboard, P0-1) already uses.
 */
export function useOrdersQuery(
  status: string | undefined,
  offset: number,
  source?: OrderSource,
): UseQueryResult<MerchantOrderPage, ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: ['orders', activeShopId, status ?? null, offset, source ?? null],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(ORDERS_PAGE_SIZE),
        offset: String(offset),
      });
      if (status !== undefined) params.set('status', status);
      if (source !== undefined) params.set('source', source);
      return apiRequest<MerchantOrderPage>(`/merchant/orders?${params.toString()}`, {
        accessToken,
        shopId: activeShopId,
      });
    },
    enabled: accessToken !== null && activeShopId !== null,
    placeholderData: (previous) => previous,
  });
}

/**
 * `PATCH /merchant/orders/{id}/status` — the API's `OrderStateMachine` is
 * the ONLY authority on which transitions are legal; this mutation sends
 * whatever the caller asks and relays the API's own error verbatim on
 * refusal (`IllegalTransitionError` → 400) — no transition table is
 * reproduced here.
 */
/**
 * Migration 0096 — `POST /merchant/orders/manual`. The caller passes ONE `idempotencyKey` per form submission (kept
 * across retries of that same submission), so a double click or a network retry returns the first order instead of
 * creating a second one. On success every order list, the dashboard figures and the stock views refresh.
 */
export function useCreateManualOrderMutation(): UseMutationResult<
  ManualOrderResult,
  ApiError,
  { body: CreateManualOrderBody; idempotencyKey: string }
> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ body, idempotencyKey }) =>
      apiRequest<ManualOrderResult>('/merchant/orders/manual', {
        method: 'POST',
        body,
        accessToken,
        shopId: activeShopId,
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orders', activeShopId] });
      void queryClient.invalidateQueries({ queryKey: ['products', activeShopId] });
      void queryClient.invalidateQueries({ queryKey: ['inventory', activeShopId] });
      void queryClient.invalidateQueries({ queryKey: ['merchant-analytics'] });
      void queryClient.invalidateQueries({ queryKey: ['analytics'] });
      void queryClient.invalidateQueries({ queryKey: ['invoices', activeShopId] });
    },
  });
}

export function useUpdateOrderStatusMutation(): UseMutationResult<
  MerchantOrder,
  ApiError,
  { orderId: string; status: MerchantDrivenStatus; reason?: string }
> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, status, reason }) =>
      apiRequest<MerchantOrder>(`/merchant/orders/${orderId}/status`, {
        method: 'PATCH',
        body: { status, ...(reason !== undefined && reason.length > 0 ? { reason } : {}) },
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orders', activeShopId] });
    },
  });
}
